import { createSignal } from 'solid-js'

import type { WeightEntry } from '../../state/weightStore'
import {
   findWeightChartPointAt,
   formatWeightChartPointDate,
   getWeightChartHoverDate,
   type WeightChartPoint,
} from './weightChartUtils'

type HoveredWeightChartPoint = {
   entry: WeightEntry
   leftPercent: number
   topPercent: number
}

type WeightChartHoverSource = {
   dataPoints: WeightEntry[]
   rangeEnd: number
   rangeStart: number
}

/**
 * Tracks what the pointer is over on the weight chart: a measurement dot, or
 * otherwise the date under the pointer's x-position.
 *
 * @param source - The chart data used for the date fallback.
 * @returns The hover signals plus handlers for the canvas.
 */
export function createWeightChartHover(source: WeightChartHoverSource) {
   let plottedPoints: WeightChartPoint[] = []
   const [hoverDate, setHoverDate] = createSignal('')
   const [hoveredPoint, setHoveredPoint] = createSignal<HoveredWeightChartPoint | null>(
      null,
   )

   /**
    * Replaces the hit-test targets after a redraw and drops any stale tooltip.
    *
    * @param points - The dots from the latest draw.
    */
   const setPlottedPoints = (points: WeightChartPoint[]) => {
      plottedPoints = points
      setHoveredPoint(null)
   }

   /**
    * Updates the hover state for the pointer position over the canvas.
    *
    * @param canvas - The chart canvas.
    * @param event - The pointer event.
    */
   const handlePointerMove = (canvas: HTMLCanvasElement, event: MouseEvent) => {
      const point = findWeightChartPointAt(
         canvas,
         event.clientX,
         event.clientY,
         plottedPoints,
      )
      setHoveredPoint(
         point ?
            {
               entry: point.entry,
               leftPercent: (point.x / canvas.width) * 100,
               topPercent: (point.y / canvas.height) * 100,
            }
         :  null,
      )
      setHoverDate(
         point ? '' : (
            getWeightChartHoverDate(
               canvas,
               event.clientX,
               source.dataPoints,
               source.rangeStart,
               source.rangeEnd,
            )
         ),
      )
   }

   /**
    * Clears all hover state when the pointer leaves the canvas.
    */
   const clearHover = () => {
      setHoverDate('')
      setHoveredPoint(null)
   }

   return { hoverDate, hoveredPoint, setPlottedPoints, handlePointerMove, clearHover }
}

type WeightChartPointTooltipProps = {
   hovered: HoveredWeightChartPoint
   formatMass: (weightKg: number) => string
}

/**
 * Highlights the hovered measurement dot and shows its weight and time.
 *
 * @param props - The hovered dot and mass formatter.
 * @returns The ring and tooltip elements.
 */
export const WeightChartPointTooltip = (props: WeightChartPointTooltipProps) => {
   /**
    * Places an overlay on the hovered dot's centre.
    *
    * @returns The inline position style.
    */
   const position = () => ({
      left: `${props.hovered.leftPercent}%`,
      top: `${props.hovered.topPercent}%`,
   })

   return (
      <>
         <span
            class="pointer-events-none absolute size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-accent"
            style={position()}
         />
         <div
            id="weight-chart-point-tooltip"
            role="tooltip"
            class="pointer-events-none absolute z-10 whitespace-nowrap rounded-md border border-edge-soft bg-surface-sunken/90 px-2 py-1 text-xs shadow-lg backdrop-blur-sm"
            classList={{
               // Keep the tooltip inside the chart near its edges.
               '-translate-x-1/2':
                  props.hovered.leftPercent >= 20 && props.hovered.leftPercent <= 80,
               '-translate-x-full': props.hovered.leftPercent > 80,
               '-translate-y-[calc(100%+0.75rem)]': props.hovered.topPercent >= 25,
               'translate-y-3': props.hovered.topPercent < 25,
            }}
            style={position()}
         >
            <p class="font-semibold text-ink">
               {props.formatMass(props.hovered.entry.weight)}
            </p>
            <p class="text-ink-soft">
               {formatWeightChartPointDate(props.hovered.entry.timestamp)}
            </p>
         </div>
      </>
   )
}
